/**
 * Search Results List
 *
 * Renders the command palette results: loading, empty, recents, or search
 * results. Used inside a cmdk <Command.List>.
 *
 * One ranked list, every type mixed: each row's icon says what it is
 * (`paletteSections.ts`). `layout="preview"` (the All tab) shows its top rows
 * and a "Show all" into the Workspace tab; `layout="full"` (the Workspace tab,
 * and the entity pickers) shows every row and grows only at the bottom, via an
 * IntersectionObserver sentinel. Related, the meaning matches, sits below the
 * ranked rows, and in the full list only once it has stopped growing, so it can
 * never push rows away from under the reader.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Box, HStack, VStack, Text } from '@chakra-ui/react';
import { Command } from 'cmdk';
import { gradientVariantForGlyph } from '../../../theme/iconGradient';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { SearchResultItem } from './SearchResultItem';
import type { SearchResult } from '../../../services/search.service';
import type { PaletteSections } from './paletteSections';

/** Where a clicked row sat, for analytics: which layout decisions earn clicks. */
export type ResultSection = 'recent' | 'preview' | 'list' | 'related';
import { BowerSpinner } from '../../ui/BowerSpinner';
import { ApiResponseError } from '../../../services/api/http-client.types';

/** What to tell the user when the search request itself failed. */
export function searchFailureMessage(error: Error): string {
  if (error instanceof ApiResponseError && error.status === 429) {
    return 'Too many searches in a row. Wait a moment, then retry.';
  }
  return "Search isn't working right now. Retry in a moment.";
}

/**
 * A failed request: the error, then a retry the arrow keys can reach. The
 * palette's live region announces the message, so this one is not an alert:
 * a live role inside the listbox is outside what a listbox may contain.
 */
export function SearchFailure({ error, onRetry }: { error?: Error | null; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <>
      <Box px={3} py={3}>
        <Text fontSize="sm" color="text.muted">
          {searchFailureMessage(error)}
        </Text>
      </Box>
      {onRetry && (
        <Command.Item
          value="search-retry"
          aria-label="Retry search"
          onSelect={onRetry}
          style={{ cursor: 'pointer' }}
        >
          <HStack px={3} py={3} spacing={3}>
            <MaterialIcon name="refresh" boxSize={4} />
            <Text fontSize="sm">Retry search</Text>
          </HStack>
        </Command.Item>
      )}
    </>
  );
}

interface SearchResultsListProps {
  isShowingRecents: boolean;
  hasResults: boolean;
  isFetching: boolean;
  showEmpty: boolean;
  filteredResults: SearchResult[];
  /** The ranked rows and Related; null renders `filteredResults` with no Related. */
  sections: PaletteSections | null;
  /** `preview`: the top rows and a "Show all"; `full`: every row, paging at the bottom. */
  layout?: 'preview' | 'full';
  onSelect: (result: SearchResult, section: ResultSection) => void;
  /** The query, so name matches can emphasise what matched. */
  query?: string;
  /** Open the full Workspace list ("Show all results in Workspace"). */
  onShowAll?: () => void;
  /** Heading over the empty-query list. */
  recentsHeading?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  /**
   * Wrap the body in a `<Command.List>`. Defaults to `true` (the standalone
   * palette). The expanded Cmd-K palette sets this to `false` so the content
   * lives inside a single shared list alongside the static command groups.
   */
  wrapInList?: boolean;
  /**
   * Replaces the default loading spinner while fetching with no results yet —
   * the expanded palette passes a reserved-height `<ContentSkeleton />` so the
   * groups below don't reflow when content arrives. Defaults to the spinner.
   */
  loadingContent?: ReactNode;
  /**
   * Suppress the "No results found" / "No recent items" placeholders so the
   * content zone silently collapses (the expanded palette always has static
   * groups + Ask Bower below). Defaults to `false`.
   */
  hideEmptyStates?: boolean;
  /**
   * The search request failed and there is nothing to show. Rendered as an
   * error with a retry, and it replaces the empty states: a request that
   * could not run must never read as one that matched nothing.
   */
  failure?: Error | null;
  onRetry?: () => void;
  /**
   * When `wrapInList`: a change starts the list at the top (the query plus
   * anything else that replaces the results, such as the type filter), as in
   * ExpandedResultGroups.
   */
  scrollResetKey?: string;
}

export function SearchResultsList({
  isShowingRecents,
  hasResults,
  isFetching,
  showEmpty,
  filteredResults,
  sections,
  layout = 'full',
  onSelect,
  query,
  onShowAll,
  recentsHeading = 'Recent',
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  wrapInList = true,
  loadingContent,
  hideEmptyStates = false,
  failure,
  onRetry,
  scrollResetKey,
}: SearchResultsListProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [scrollResetKey]);
  const suppressEmptyStates = hideEmptyStates || !!failure;

  // IntersectionObserver triggers onLoadMore when sentinel scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !onLoadMore || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { rootMargin: '100px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasNextPage, isFetchingNextPage]);

  const body = (
    <>
      {/* Loading state */}
      {isFetching && !hasResults && (
        <Command.Loading>{loadingContent ?? <BowerSpinner size="sm" />}</Command.Loading>
      )}

      {/* Empty state */}
      {!suppressEmptyStates && showEmpty && (
        <Command.Empty>
          <MaterialIcon
            name="search_off"
            boxSize={10}
            gradient={gradientVariantForGlyph('search_off')}
          />
          <Text fontSize="sm" color="text.muted">
            No results found
          </Text>
        </Command.Empty>
      )}

      {/* No recent items state */}
      {!suppressEmptyStates && isShowingRecents && !hasResults && !isFetching && (
        <VStack py={8} spacing={3}>
          <MaterialIcon name="schedule" boxSize={10} gradient="warm" />
          <Text fontSize="sm" color="text.muted">
            No recent items
          </Text>
        </VStack>
      )}

      {/* Recent items (ungrouped) */}
      {isShowingRecents && hasResults && (
        <Command.Group heading={recentsHeading} forceMount>
          {filteredResults.map((result) => (
            <SearchResultItem
              key={result.id}
              result={result}
              onSelect={(r) => onSelect(r, 'recent')}
            />
          ))}
        </Command.Group>
      )}

      {!isShowingRecents && hasResults && (
        <Command.Group heading={sections ? 'Workspace' : undefined} forceMount>
          {(sections?.rows ?? filteredResults).map((result) => (
            <SearchResultItem
              key={result.id}
              result={result}
              onSelect={(r) => onSelect(r, layout === 'preview' ? 'preview' : 'list')}
              showSnippet
              query={query}
            />
          ))}
          {layout === 'preview' &&
            onShowAll &&
            sections &&
            sections.total > sections.rows.length && (
              <Command.Item
                value="show-all-workspace"
                onSelect={onShowAll}
                style={{ cursor: 'pointer' }}
              >
                <HStack px={3} py={2.5} spacing={3}>
                  {/* Aligns with the 32px icon tiles above, so the row reads as
                    the list's own continuation rather than a stray button. */}
                  <Box w="32px" display="flex" justifyContent="center" flexShrink={0}>
                    <MaterialIcon name="arrow_forward" boxSize={4} color="text.secondary" />
                  </Box>
                  {/* No count: the server's total is a floor (it ranks a bounded
                      candidate pool) and grows as pages load, so a number here
                      would read as exact and then change. */}
                  <Text fontSize="sm" color="text.secondary">
                    Show all results in Workspace
                  </Text>
                </HStack>
              </Command.Item>
            )}
        </Command.Group>
      )}

      {/* Infinite scroll sentinel + loading spinner: the full list only. */}
      {!isShowingRecents && hasResults && layout === 'full' && hasNextPage && (
        <Box ref={sentinelRef} display="flex" justifyContent="center" py={3}>
          {isFetchingNextPage && <BowerSpinner size="sm" />}
        </Box>
      )}

      {/* Meaning matches the ranked rows did not show. Below them, and in the
          full list only once it has stopped growing. */}
      {!isShowingRecents &&
        sections &&
        sections.related.length > 0 &&
        (layout === 'preview' || !hasNextPage) && (
          <Command.Group heading="Similar in meaning" forceMount>
            {sections.related.map((result) => (
              <SearchResultItem
                key={result.id}
                result={result}
                onSelect={(r) => onSelect(r, 'related')}
                showSnippet
              />
            ))}
          </Command.Group>
        )}

      {/* Failed request, first page or a later one: an error with a retry,
          never an empty state. Last, so a failed page 2 sits below page 1. */}
      <SearchFailure error={failure} onRetry={onRetry} />
    </>
  );

  return wrapInList ? <Command.List ref={listRef}>{body}</Command.List> : body;
}
